/*
 * Copyright (c) 2023-2026 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { Form, FormInstance, FormItemProps, FormProps, Input, Select } from 'antd'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { nanoid } from 'nanoid'
import { FieldContext } from 'rc-field-form'
import { InternalNamePath, NamePath } from 'rc-field-form/lib/interface'
import { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { PHONE_CODES } from '../../constants/generated/phone-codes'
import { NORTH_AMERICA_REGION_CODES } from '../../constants/phone-codes'
import { PhoneCodeInfo } from '../../models/zones'
import { LabelValue } from '../../types/additional-ui-types'
import { FormInputLabel } from '../FormInputLabel/FormInputLabel'
import { FormItemStatusForwarder } from '../FormItemStatusForwarder/FormItemStatusForwarder'

interface DefaultProps {
  placeholder?: string
  // TODO: maybe we don't really need this since password and confirmation password are always required?
  rulesCreatorFunction?: (phoneCountryCode: string, phoneNumber: string) => FormItemProps['rules']
  layout: FormProps['layout']
}

const DEFAULT_PHONE_INFO = { name: '', phoneCode: '' }
const DEFAULT_RULES: NonNullable<FormItemProps['rules']> = []
export const PHONE_CODE_OPTIONS: InputPhoneLabelValue[] = Object.entries(PHONE_CODES).map(([countryISO2Code, value]) =>
  getPhoneInfoLabelValue(countryISO2Code, value)
)

const phoneNumberUtilInstance = PhoneNumberUtil.getInstance()

export type InputPhoneLabelValue = LabelValue<ReactNode> & { title: string }

export function InputPhone({ placeholder, layout, rulesCreatorFunction, ...formItemProps }: DefaultProps & Omit<FormItemProps, 'rules'>) {
  // TODO: these share a lot of parts with InputTags, perhaps we can create some kind of hook?
  const form = Form.useFormInstance() as FormInstance | undefined
  const context = useContext(FieldContext)

  const { name, label, className, ...restFormItemProps } = formItemProps
  const mergedName = useRef(getMergedNamePath(context.prefixName, name))

  // Store the merged name string here, so that it doesn't change on each render.
  const mergedNameString = useRef(getMergedNameString(mergedName.current))
  const [errors, setErrors] = useState<ReactNode[] | null>(null)

  const phoneNumberFormValue = Form.useWatch(mergedName.current, form)
  const [initialPhoneInfo] = useState(() => getPhoneInfoAndPhoneNumber(form?.getFieldValue(mergedName.current)))

  const [phoneCodeInfo, setPhoneCodeInfo] = useState<InputPhoneLabelValue>(
    getPhoneInfoLabelValue(initialPhoneInfo?.countryISO2Code ?? '', initialPhoneInfo?.phoneInfo ?? DEFAULT_PHONE_INFO)
  )
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneInfo?.phoneNumber ?? '')
  const isChangedOnce = useRef(initialPhoneInfo !== undefined)

  useEffect(() => {
    // This logic here may be very much shortened if the data structure actually has country code and phone number.
    if (!form || !phoneNumberFormValue || isChangedOnce.current) return

    // In normal cases, `phoneNumberFormValue` should always be the same as `phoneCodeInfo` and `phoneNumber` when we update it normally.
    // However, in the initial rendering phases, after restoration from external source, `phoneCodeInfo` and `phoneNumber` can "lag"
    // behind `phoneNumberFormValue` and that's exactly why we need to sync it here.
    //
    // This should happen at maximum once. We shouldn't bother in creating like `isMounted` ref because at the end of the day
    // we'll be checking the `if` every effect fire anyway.
    try {
      const parseResult = getPhoneInfoAndPhoneNumber(phoneNumberFormValue)
      if (!parseResult) return

      setPhoneCodeInfo(getPhoneInfoLabelValue(parseResult.countryISO2Code, parseResult.phoneInfo))
      setPhoneNumber(parseResult.phoneNumber)

      isChangedOnce.current = true
    } catch (err) {
      console.error(err)
    }
  }, [form, phoneNumberFormValue])

  function updateFormState(nextCountryISO2Code: string, nextPhoneNumber: string) {
    const effectivePhoneCode = PHONE_CODES[nextCountryISO2Code]?.phoneCode || ''
    let effectivePhoneNumber = nextPhoneNumber
    if (effectivePhoneNumber.startsWith('+')) {
      effectivePhoneNumber = effectivePhoneNumber.slice(1)
    }

    // Phone code will always exist, but phone number may not (empty string).
    form?.setFieldValue(mergedName.current, `+${effectivePhoneCode}${effectivePhoneNumber}`)
    form?.validateFields()
  }

  const computedRules = rulesCreatorFunction?.(phoneCodeInfo.value, phoneNumber) ?? DEFAULT_RULES
  const isRequired = !!computedRules?.find(rule => typeof rule === 'object' && rule.required)

  return (
    <div className="relative">
      {name && (
        <div className="invisible absolute" aria-hidden="true">
          <Form.Item name={mergedName.current} rules={rulesCreatorFunction?.(phoneCodeInfo.value, phoneNumber)} help={<></>}>
            {/* Input needs to be the first child of `Form.Item`, so to hack this around, */}
            {/* we need to have `FormItemStatusForwarder` inside the `prefix` so that it can be used. */}
            {/* The `help` prop above gets set into empty Fragment to "hack" around so the error message doesn't show in 2 places. */}
            <Input prefix={<FormItemStatusForwarder setErrors={setErrors} />} />
          </Form.Item>
        </div>
      )}

      <Form.Item
        help={errors ?? restFormItemProps.help}
        validateStatus={errors ? 'error' : undefined}
        htmlFor={mergedNameString.current}
        className={className}
        label={<FormInputLabel layout={layout}>{label}</FormInputLabel>}
        colon={layout !== 'horizontal'}
        required={isRequired}>
        <Input
          id={mergedNameString.current}
          placeholder={placeholder}
          value={phoneNumber}
          autoComplete="off"
          prefix={
            <Select
              showSearch
              // Custom filterOption to filter based on option title
              filterOption={(inputValue, option) => !!option?.title.toLowerCase().includes(inputValue.toLowerCase())}
              onClick={e => e.stopPropagation()}
              value={initialPhoneInfo?.phoneInfo ? phoneCodeInfo : undefined}
              title={`Currently selected country: ${phoneCodeInfo.value === '' ? '-' : phoneCodeInfo.title}`}
              aria-label="Country phone code"
              onChange={(_, option) => {
                if (!Array.isArray(option)) {
                  setPhoneCodeInfo(option)
                  updateFormState(option.value, phoneNumber)
                  isChangedOnce.current = true
                }
              }}
              options={PHONE_CODE_OPTIONS}
              // Need to have fixed width because otherwise
              // rc-virtual won't work.
              className="!w-[200px] p-0 ab-phone-input"
              placeholder="Select phone code"
            />
          }
          className="py-0 pl-0"
          onChange={e => {
            setPhoneNumber(e.target.value)
            updateFormState(phoneCodeInfo.value, e.target.value)
          }}
        />
      </Form.Item>
    </div>
  )
}

// Composing functions.
// Helper functions.
// TODO: extract these 2 into utils.
function getMergedNamePath(prefix: InternalNamePath | undefined, name: NamePath | undefined) {
  if (!name) return []
  return (prefix ?? []).concat(name)
}

function getMergedNameString(paths: Array<string | number>) {
  // If paths length is 0, it's usually because of it's loose and uncontrolled (not tied to a `<Form>` component).
  // In that case, we can just generate a random ID.
  if (paths.length === 0) return nanoid(5)

  // We need to concat it with `input`, otherwise it'll clash with the hidden input.
  return paths.concat('input').join('_')
}

function getPhoneInfoLabelValue(countryISO2Code: string, phoneCodeInfo: PhoneCodeInfo) {
  return {
    label:
      countryISO2Code !== '' ? (
        <div className="flex w-full justify-between">
          <div className="flex gap-x-2">
            <img
              src={`/static/country-flag-icons/3x2/${countryISO2Code}.svg`}
              loading="lazy"
              className="w-[25px]"
              alt={`flag of the country with ISO2 code ${countryISO2Code}`}
            />
            <span className="overflow-hidden overflow-ellipsis">{phoneCodeInfo.name}</span>
          </div>
          <span>+{phoneCodeInfo.phoneCode}</span>
        </div>
      ) : (
        ''
      ),
    title: `${phoneCodeInfo.name} (+${phoneCodeInfo.phoneCode})`,
    value: countryISO2Code
  }
}

function getPhoneInfoAndPhoneNumber(value: string) {
  try {
    const parsed = phoneNumberUtilInstance.parse(value)
    const parsedCountryCode = parsed.getCountryCode()
    const parsedPhoneNumber = parsed.getNationalNumber()

    // If parsing result is invalid, then early return.
    if (!parsedCountryCode || !parsedPhoneNumber) return

    // US and Canada share the +1 prefix, so we need to check the region codes.
    let effectiveCountryISO2Code = phoneNumberUtilInstance.getRegionCodeForCountryCode(parsedCountryCode)
    if (effectiveCountryISO2Code === 'US') {
      const regionCode = Number(parsedPhoneNumber.toString().slice(0, 3))

      for (const key in NORTH_AMERICA_REGION_CODES) {
        const hasRegionCode = NORTH_AMERICA_REGION_CODES[key].includes(regionCode)
        if (hasRegionCode) {
          effectiveCountryISO2Code = key as libphonenumber.RegionCode
          break
        }
      }
    }

    const matchingEntry = PHONE_CODES[effectiveCountryISO2Code]
    // If we don't match the ones existing in the select options, then early return.
    if (!matchingEntry) return

    return { phoneInfo: matchingEntry, phoneNumber: `${parsedPhoneNumber}`, countryISO2Code: effectiveCountryISO2Code }
  } catch (err) {
    return undefined
  }
}
